import { isString, typeGuardGenerator } from 'tcheck';

/**
 * Magic link base URL without leading slash
 * @returns {string} The base URL
 */
function getBaseUrl() {
  return import.meta.env.VITE_APP_MAGIC_LINK_BASE_URL ?? '';
}

/**
 * Begin the magic link authentication flow by requesting a magic link.
 * @param {object} payload The payload
 * @param {string} payload.username The user's email address
 */
export async function requestMagicLink(payload) {
  const { username } = payload;
  const url = `${getBaseUrl()}/magicLinkRequest`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify({
      username,
    }),
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const json = await response.json();

  if (!response.ok) {
    const error = isString(json.error) ? json.error : 'Unknown Error';
    throw new Error(error);
  }
}

/**
 * @typedef {object} MagicLinkResponse
 * @property {string} accessToken Cognito Access Token
 * @property {string} idToken Cognito ID Token
 * @property {string} refreshToken Cognito Refresh Token
 */

/** @type {import('tcheck').TypeGuard<MagicLinkResponse>} */
const isMagicLinkResponse = typeGuardGenerator({
  accessToken: isString,
  idToken: isString,
  refreshToken: isString,
});

/**
 * Verify a magic link by verifying the message and signature
 * @param {object} payload
 * @param {string} payload.message The message from a link to verify
 * @param {string} payload.signature The signature from a link to verify
 * @returns {Promise<MagicLinkResponse>} The tokens
 */
export async function verifyMagicLink(payload) {
  const { message, signature } = payload;
  const url = `${getBaseUrl()}/magicLinkVerify`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify({
      message,
      signature,
    }),
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const json = await response.json();

  if (!response.ok) {
    const error = isString(json.error) ? json.error : 'Unknown Error';
    throw new Error(error);
  }

  if (!isMagicLinkResponse(json)) {
    throw new Error('Invalid response');
  }

  return {
    accessToken: json.accessToken,
    idToken: json.idToken,
    refreshToken: json,
  };
}
